// The recipe tree as a list of market search terms.
//
// The market analyzer's GUI takes an "Only items" string and its parser is
// `tuple(term.strip() for term in spec.split(",") if term.strip())` — a
// comma-separated list of the things you would type into the broker's own
// search box, one after another. So this module's whole job is to turn a
// recipe's materials into exactly those words, and nothing here may invent a
// spelling the broker would not match.
//
// Outside the component for the same reason recipeSort is: vitest here runs in
// `environment: "node"` with no renderer, and which names end up in a pasted
// string is exactly the kind of thing that goes wrong silently — the scan comes
// back short and nobody can say which material was dropped.
import type { RecipeDetail, RecipeNode } from "../api/types";

export interface MaterialTerm {
  /** The item name, exactly as the tree spells it — this is the search term. */
  name: string;
  /** Indent level: the recipe's direct materials are 0, their materials 1, … */
  depth: number;
  /** Stable React key; unique even for a name that appears twice. */
  key: string;
  /** A later appearance of a name already listed above. Rendered dimmed and
   *  with no checkbox of its own. */
  duplicate: boolean;
  /** This material has never been scanned (`resolved: false`). */
  unscanned: boolean;
  /** This row is the recipe's own scroll, not a material from the tree. */
  scroll: boolean;
}

/** The names came from CITEXT columns and the broker is not case-sensitive
 *  either, so two rows differing only in case are one search term. */
function fold(name: string): string {
  return name.trim().toLowerCase();
}

export function materialTerms(detail: RecipeDetail): MaterialTerm[] {
  const terms: MaterialTerm[] = [];
  // Folded names, in the order they were first rendered.
  const listed = new Set<string>();

  function walk(nodes: RecipeNode[], depth: number): void {
    for (const node of nodes) {
      // Adena is `resolved` in the cost engine because it IS the currency —
      // every cost is already denominated in it, so the engine has nothing to
      // look up. That makes it the one row in the tree that is not a purchase,
      // and a scan for "adena" would sweep the broker for a thing nobody
      // lists. Its subtree goes with it: a currency has no materials, and if
      // one ever appeared it would be an artefact of the same confusion.
      if (fold(node.name) === "adena") continue;

      // First in *render* order owns the checkbox, not the shallowest
      // occurrence. The rows below are indented exactly as the Materials table
      // indents them, so "listed above" has to mean literally above — reorder
      // the ownership and the label points at a row the reader has to scroll
      // down to find, on the one screen where every row is the same word.
      const duplicate = listed.has(fold(node.name));
      if (!duplicate) listed.add(fold(node.name));

      terms.push({
        name: node.name,
        depth,
        // The ordinal, not the path: a recipe may list the same material twice
        // under the same parent, and a path key would then collide with itself.
        key: `${terms.length}:${node.name}`,
        duplicate,
        // Never scanned under any alias — a fact about our data, not about the
        // market. These are the rows a scan exists to fill, so they are listed
        // and checked like every other one.
        unscanned: !node.resolved,
        scroll: false,
      });
      walk(node.children, depth + 1);
    }
  }

  // The root is the crafted item itself: the thing the scan is for, not a thing
  // to scan. The whole tree is walked regardless of which rows the page has
  // expanded and regardless of its buy/craft mode — a scan list is what to go
  // and look at, not what the current reading of the cost happened to spend.
  walk(detail.tree.children, 0);

  // The scroll is a material on 610 of the 995 catalogue recipes, because a
  // craft consumes it — so when the page is counting scrolls it is already in
  // the tree above and appending it here would list it twice. Appended rather
  // than prepended so the tree above keeps its own shape, and included even
  // when it has never been scanned: a scroll only a vendor sells is precisely
  // the thing a reader would want to go and look for.
  if (!listed.has(fold(detail.name))) {
    terms.push({
      name: detail.name,
      depth: 0,
      key: `${terms.length}:${detail.name}`,
      duplicate: false,
      // The scroll has no `resolved` flag of its own; a null item_id is the
      // same fact, and it is what the panel at the foot of the page reads to
      // say "has never been scanned".
      unscanned: detail.scroll.item_id === null,
      scroll: true,
    });
  }

  return terms;
}

/** The "Only items" string. Bare commas and no spaces, because the analyzer
 *  splits on "," and the reader pastes this whole line into a field — a
 *  prettified ", " would survive that parser's own strip() but not survive a
 *  reader who trims the line by eye and leaves a trailing comma behind. Empty
 *  terms are dropped here for the same reason the parser drops them. */
export function joinTerms(names: string[]): string {
  return names.map((name) => name.trim()).filter((name) => name !== "").join(",");
}
